import { inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import {
  payrollBatchesActions,
  payrollSummaryActions,
  runningPayrollActions,
  tdsCalculationActions,
  userPayrollActions
} from './payroll.actions'
import { catchError, map, of, switchMap } from 'rxjs'
import { PayrollService } from '../../services/payroll.service'
import { ToastrService } from 'ngx-toastr'
import { Router } from '@angular/router'
import { eps } from '../../services/url.service'
import { PayrollSummaryService } from '../../services/payroll-summary.service'

// Load all payroll batches
export const loadPayrollBatches$ = createEffect(
  (
    actions$ = inject(Actions),
    payrollService = inject(PayrollService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(payrollBatchesActions.loadPayrollBatches),
      switchMap(({ financialYear, belongsTo }) =>
        payrollService.loadPayrollBatches(financialYear, belongsTo).pipe(
          map(payrollBatches =>
            payrollBatchesActions.loadPayrollBatchesSuccess({ payrollBatches })
          ),
          catchError(error => {
            toastr.error(error.error.message)
            return of(payrollBatchesActions.loadPayrollBatchesFailure({ error: error.message }))
          })
        )
      )
    ),
  { functional: true }
)

// Load Running Payroll batch
export const loadRunningPayroll$ = createEffect(
  (
    actions$ = inject(Actions),
    payrollService = inject(PayrollService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(runningPayrollActions.loadRunningPayroll),
      switchMap(({ payPeriodStart, payPeriodEnd, belongsTo }) =>
        payrollService.loadRunningPayrollBatch(payPeriodStart, payPeriodEnd, belongsTo).pipe(
          map(runningPayrollBatch =>
            runningPayrollActions.loadRunningPayrollSuccess({ updatedBatch: runningPayrollBatch })
          ),
          catchError(error => {
            toastr.error(error.error.message)
            return of(runningPayrollActions.loadRunningPayrollFailure({ error: error.message }))
          })
        )
      )
    ),
  {
    functional: true
  }
)

// Load previous salaries
export const loadPreviousSalaries$ = createEffect(
  (
    actions$ = inject(Actions),
    payrollService = inject(PayrollService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(runningPayrollActions.loadPreviousSalaries),
      switchMap(({ requests }) =>
        payrollService.loadPreviousSalaries(requests).pipe(
          map(previousSalaries =>
            runningPayrollActions.loadPreviousSalariesSuccess({ previousSalaries })
          ),
          catchError(error => {
            toastr.error(error.error.message)
            return of(runningPayrollActions.loadPreviousSalariesFailure({ error: error.message }))
          })
        )
      )
    ),
  { functional: true }
)

// Load arrear lop days
export const loadArrearLopDays$ = createEffect(
  (
    actions$ = inject(Actions),
    payrollService = inject(PayrollService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(runningPayrollActions.loadArrearLopDays),
      switchMap(({ requests }) =>
        payrollService.loadArrearLopDays(requests).pipe(
          map(arrearLopDays => runningPayrollActions.loadArrearLopDaysSuccess({ arrearLopDays })),
          catchError(error => {
            toastr.error(error.error.message)
            return of(runningPayrollActions.loadArrearLopDaysFailure({ error: error.message }))
          })
        )
      )
    ),
  { functional: true }
)

//Load Batch TDS Preview
export const loadBatchTdsPreview$ = createEffect(
  (
    actions$ = inject(Actions),
    payrollService = inject(PayrollService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(tdsCalculationActions.loadBatchTdsPreview),
      switchMap(({ payload }) =>
        payrollService.loadBatchTdsPreview(payload).pipe(
          map(tdsResults => tdsCalculationActions.loadBatchTdsPreviewSuccess(tdsResults)),
          catchError(error => {
            toastr.error(error.error.message)
            return of(tdsCalculationActions.loadBatchTdsPreviewFailure({ error: error.message }))
          })
        )
      )
    ),
  { functional: true }
)

//Load TDS Preview
export const loadTdsPreview$ = createEffect(
  (
    actions$ = inject(Actions),
    payrollService = inject(PayrollService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(tdsCalculationActions.loadTdsPreview),
      switchMap(({ payload }) =>
        payrollService.loadTdsPreview(payload).pipe(
          map(tdsResult => tdsCalculationActions.loadTdsPreviewSuccess(tdsResult)),
          catchError(error => {
            toastr.error(error.error.message)
            return of(tdsCalculationActions.loadTdsPreviewFailure({ error: error.message }))
          })
        )
      )
    ),
  { functional: true }
)

// Save Running Payroll Batch draft
export const saveRunningPayrollBatchDraft$ = createEffect(
  (
    actions$ = inject(Actions),
    payrollService = inject(PayrollService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(runningPayrollActions.saveRunningPayrollDraft),
      switchMap(({ batchData }) =>
        payrollService.upsertRunningPayrollBatch(batchData).pipe(
          map(batchData => runningPayrollActions.saveRunningPayrollDraftSuccess({ batchData })),
          catchError(error => {
            toastr.error(error.error.message)
            return of(
              runningPayrollActions.saveRunningPayrollDraftFailure({ error: error.message })
            )
          })
        )
      )
    ),
  { functional: true }
)

// Update the running payroll status
export const updateRunningPayrollStatus$ = createEffect(
  (
    actions$ = inject(Actions),
    payrollService = inject(PayrollService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(runningPayrollActions.updateRunningPayrollStatus),
      switchMap(({ id, status, payrollIds }) => {
        return payrollService.updatePayrollStatus({ id, status, payrollIds }).pipe(
          map(batchData => {
            toastr.success('Payroll Run Successful!')
            return runningPayrollActions.updateRunningPayrollStatusSuccess({ batchData })
          }),
          catchError(error => {
            toastr.error(error.error.message)
            return of(
              runningPayrollActions.updateRunningPayrollStatusFailure({ error: error.message })
            )
          })
        )
      })
    ),
  { functional: true }
)

// Delete the payroll batch
export const deletePayrollBatch$ = createEffect(
  (
    actions$ = inject(Actions),
    payrollService = inject(PayrollService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(payrollBatchesActions.deletePayrollBatch),
      switchMap(({ id }) =>
        payrollService.deletePayrollBatch(id).pipe(
          map(({ id }) => {
            toastr.success('Payroll batch deleted successfully')
            return payrollBatchesActions.deletePayrollBatchSuccess({ id })
          }),
          catchError(error => {
            toastr.error(error.error.message)
            return of(payrollBatchesActions.deletePayrollBatchFailure({ error: error.message }))
          })
        )
      )
    ),
  { functional: true }
)

// Load a single user payroll data
export const loadUserPayrollData$ = createEffect(
  (
    actions$ = inject(Actions),
    payrollService$ = inject(PayrollService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(userPayrollActions.loadUserPayroll),
      switchMap(({ payPeriodStart, payPeriodEnd, employeeId }) =>
        payrollService$.getUserPayrollData(payPeriodStart, payPeriodEnd, employeeId).pipe(
          map(payroll => userPayrollActions.loadUserPayrollSuccess({ payroll })),
          catchError(error => {
            toastr.error(error.error.message)
            return of(userPayrollActions.loadUserPayrollFailure({ error: error.message }))
          })
        )
      )
    ),
  { functional: true }
)

// Load the latest user payroll date
export const loadLatestUserPayrollDate$ = createEffect(
  (
    actions$ = inject(Actions),
    payrollService$ = inject(PayrollService),
    router = inject(Router),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(userPayrollActions.loadLatestUserPayrollDate),
      switchMap(({ employeeId }) =>
        payrollService$.getLatestUserPayrollDate(employeeId).pipe(
          map(latestPayrollDate =>
            userPayrollActions.loadLatestUserPayrollDateSuccess(latestPayrollDate)
          ),
          catchError(error => {
            toastr.error(error.error.message)
            router.navigate([eps.hrm.employeePath, employeeId])
            return of(userPayrollActions.loadLatestUserPayrollDateFailure({ error: error.message }))
          })
        )
      )
    ),
  { functional: true }
)

//Finalize Payroll Batch
export const finalizePayrollBatch$ = createEffect(
  (
    actions$ = inject(Actions),
    payrollService = inject(PayrollService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(payrollBatchesActions.finalizePayroll),
      switchMap(({ batchId, dateOfPayment, modeOfPayment, paidBy }) =>
        payrollService.finalizePayrollBatch(batchId, dateOfPayment, modeOfPayment, paidBy).pipe(
          map(batchData => {
            toastr.success('Payroll batch finalized successfully')
            return payrollBatchesActions.finalizePayrollSuccess({ batch: batchData })
          }),
          catchError(error => {
            toastr.error(error.error.message)
            return of(payrollBatchesActions.finalizePayrollFailure({ error: error.message }))
          })
        )
      )
    ),
  { functional: true }
)

//Load Payroll Summary
const loadPayrollSummary$ = createEffect(
  (
    actions$ = inject(Actions),
    payrollSummaryService = inject(PayrollSummaryService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(payrollSummaryActions.loadSummary),
      switchMap(({ employeeId, financialYear, joiningDate }) =>
        payrollSummaryService.getPayrollSummary(employeeId, financialYear, joiningDate).pipe(
          map(summary => payrollSummaryActions.loadSummarySuccess({ summary })),
          catchError(error => {
            toastr.error(error.error.message)
            return of(payrollSummaryActions.loadSummaryFailure({ error: error.message }))
          })
        )
      )
    ),
  { functional: true }
)

export const payrollEffects = {
  loadPayrollBatches$,
  loadRunningPayroll$,
  loadPreviousSalaries$,
  loadArrearLopDays$,
  loadBatchTdsPreview$,
  loadTdsPreview$,
  saveRunningPayrollBatchDraft$,
  updateRunningPayrollStatus$,
  deletePayrollBatch$,
  loadUserPayrollData$,
  loadLatestUserPayrollDate$,
  finalizePayrollBatch$,
  loadPayrollSummary$
}
